import React from "react"
import { useTranslation } from "react-i18next"
import type { Team, TeamMember } from "../../../../constants/types"
import Badge from "./badge"
import ExtraMenu from "./extraMenu"

type MembersTableProps = {
  team?: Team
  members?: TeamMember[]
  displayActions: boolean
  onAddUser?: () => void
  onAction?: (action: "edit" | "remove", member: TeamMember) => void
}

const TableHeaderCell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <th
    scope="col"
    className="tw-px-6 tw-py-3 tw-text-left tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-gray-500">
    {children}
  </th>
)

const MembersTable: React.FC<MembersTableProps> = ({ members = [], displayActions, onAddUser, onAction }) => {
  const { t } = useTranslation()

  return (
    <>
      <div className="tw-mb-6 tw-flex tw-items-center tw-justify-between tw-gap-4">
        <div>
          <h4 className="tw-mb-8">
            {t("account.team.membersTitle")}
            <span className="tw-text-dark-50"> ({members.length})</span>
          </h4>
          <p className="tw-mt-1 tw-text-sm tw-text-dark">{t("account.team.membersInfo")}</p>
        </div>
        {displayActions && (
          <button type="button" onClick={onAddUser} className="tw-btn tw-btn-primary">
            {t("account.team.button.invite")}
          </button>
        )}
      </div>
      <div className="tw-rounded-2xl tw-border tw-border-gray-100">
        <table className="tw-min-w-full tw-divide-y tw-divide-gray-100">
          <thead className="tw-bg-gray-50/50">
            <tr>
              <TableHeaderCell>{t("account.team.table.name")}</TableHeaderCell>
              <TableHeaderCell>{t("account.team.table.email")}</TableHeaderCell>
              <TableHeaderCell>{t("account.team.table.role")}</TableHeaderCell>
              <TableHeaderCell>{t("account.team.table.status")}</TableHeaderCell>
              <th className="tw-w-12" />
            </tr>
          </thead>
          <tbody className="tw-divide-y tw-divide-gray-100 tw-bg-white">
            {members.map((member: TeamMember, index: number) => (
              <tr key={index} className="hover:tw-bg-gray-50/40">
                <td className="tw-whitespace-nowrap tw-px-6 tw-py-4 tw-text-sm tw-font-medium tw-text-gray-900">
                  {member?.firstName} {member?.lastName}
                </td>
                <td className="tw-whitespace-nowrap tw-px-6 tw-py-4 tw-text-sm tw-text-dark">
                  {member?.email ?? member.invitedEmail}
                </td>
                <td className="tw-whitespace-nowrap tw-px-6 tw-py-4 tw-text-sm tw-text-dark">
                  {t("common.roles." + member.role)}
                </td>
                <td className="tw-whitespace-nowrap tw-px-6 tw-py-4">
                  {member.status === "active" ? (
                    <Badge label={t("account.team.status.active")} tone="success" />
                  ) : (
                    <Badge label={t("account.team.status.pending")} tone="warning" />
                  )}
                </td>

                {displayActions && (
                  <td className="tw-px-6 tw-py-4 tw-text-right">
                    {member.role !== "account_holder" && (
                      <ExtraMenu //
                        onRemove={() => onAction?.("remove", member)}
                        onEdit={() => onAction?.("edit", member)}
                      />
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}

export default MembersTable
